// Customer View — the full support page a sub-account user sees. Browse the agency's docs,
// videos, and tours (search + tabs + video modal), see their PLAN with upgrade/downgrade
// options, get nudged to unlock plan-gated content, and file/track support TICKETS.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { customerApi, setCustomerScope } from '../lib/customerApi';
import type { LockedBundle, TicketsMetaResponse } from '../lib/customerApi';
import type { DocItem, Me, VideoItem } from '../lib/types';
import type { PlanContext, Ticket, TicketComment } from '../lib/suite-types';
import { DocCard, DocReader, TourCard, VideoCard, VideoModal } from '../components/ContentCards';
import { formatApiDate } from '../lib/time';

type Tab = 'all' | 'docs' | 'videos' | 'tours' | 'plans' | 'tickets';

export default function CustomerView({ me }: { me: Me }) {
  // Scope every customer API call to the resolved identity (SSO in prod / ?as in dev) BEFORE
  // any child fetches — the Worker refuses (400) identity-less requests rather than silently
  // scoping them to the demo agency, so tickets/meta/plans need these params to resolve.
  setCustomerScope({ agencyId: me.agencyId, locationId: me.locationId, userId: me.user.userId });
  const [bundle, setBundle] = useState<LockedBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('all');
  const [q, setQ] = useState('');
  const [playing, setPlaying] = useState<VideoItem | null>(null);
  const [reading, setReading] = useState<DocItem | null>(null);

  // Fire an analytics event, best-effort, with identity attached.
  const track = useCallback(
    (event: string, area?: string | null, meta?: unknown) =>
      void customerApi.event({
        agencyId: me.agencyId,
        locationId: me.locationId,
        userId: me.user.userId,
        event,
        area: area ?? null,
        meta,
      }),
    [me],
  );

  useEffect(() => {
    let cancelled = false;
    customerApi.library(me.agencyId)
      .then((b) => { if (!cancelled) setBundle(b); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    track('support_page_open');
    return () => { cancelled = true; };
  }, [me, track]);

  const filtered = useMemo(() => {
    if (!bundle) return null;
    const needle = q.trim().toLowerCase();
    const match = (t: string, s?: string | null) =>
      !needle || t.toLowerCase().includes(needle) || (s ?? '').toLowerCase().includes(needle);
    return {
      docs: bundle.docs.filter((d) => match(d.title, d.summary)),
      videos: bundle.videos.filter((v) => match(v.title, v.summary)),
      tours: bundle.tours.filter((t) => match(t.title, t.summary)),
    };
  }, [bundle, q]);

  const counts = filtered
    ? { docs: filtered.docs.length, videos: filtered.videos.length, tours: filtered.tours.length }
    : { docs: 0, videos: 0, tours: 0 };
  const total = counts.docs + counts.videos + counts.tours;

  const isContentTab = tab === 'all' || tab === 'docs' || tab === 'videos' || tab === 'tours';
  const showDocs = tab === 'all' || tab === 'docs';
  const showVideos = tab === 'all' || tab === 'videos';
  const showTours = tab === 'all' || tab === 'tours';

  // Nudge de-dupe: fire `plan_nudge_shown` at most once per locked item per session.
  const nudged = useRef<Set<string>>(new Set());
  const onNudgeShown = useCallback((key: string, planId: string | null, area?: string | null) => {
    if (nudged.current.has(key)) return;
    nudged.current.add(key);
    track('plan_nudge_shown', area, { item: key, plan: planId });
  }, [track]);

  const goUpgrade = useCallback((key: string, planId: string | null, area?: string | null) => {
    track('plan_nudge_click', area, { item: key, plan: planId });
    setTab('plans');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [track]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <header className="bg-gradient-to-br from-brand to-indigo-700 text-white">
        <div className="mx-auto max-w-5xl px-6 py-12">
          <p className="text-sm font-medium uppercase tracking-wide text-white/70">Help Center</p>
          <h1 className="mt-1 text-3xl font-semibold">How can we help{me.user.name ? `, ${me.user.name.split(' ')[0]}` : ''}?</h1>
          <p className="mt-2 max-w-xl text-white/80">Browse guides, watch tutorials, take a quick tour, or reach our support team.</p>
          {isContentTab && (
            <div className="mt-6">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles, videos, tours…"
                className="w-full max-w-lg rounded-xl border-0 px-4 py-3 text-slate-900 shadow-lg outline-none ring-2 ring-transparent focus:ring-white/60"
              />
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {([
            ['all', `All (${total})`],
            ['docs', `Articles (${counts.docs})`],
            ['videos', `Videos (${counts.videos})`],
            ['tours', `Tours (${counts.tours})`],
            ['plans', 'Plans'],
            ['tickets', 'Support'],
          ] as [Tab, string][]).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                tab === id ? 'bg-brand text-brand-fg' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Plans */}
        {tab === 'plans' && <PlansPanel me={me} onEvent={track} />}

        {/* Support / tickets */}
        {tab === 'tickets' && <TicketsPanel me={me} contextArea={bundle?.area ?? null} />}

        {/* Content tabs */}
        {isContentTab && (
          <>
            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
                Couldn’t load content: {error}
              </div>
            )}

            {!bundle && !error && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-40 animate-pulse rounded-xl bg-slate-200/70" />
                ))}
              </div>
            )}

            {filtered && total === 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                No results{q ? ` for “${q}”` : ''}.
              </div>
            )}

            {filtered && total > 0 && (
              <div className="space-y-10">
                {showVideos && filtered.videos.length > 0 && (
                  <section>
                    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Videos</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filtered.videos.map((v) => (v.locked ? (
                        <LockedCard
                          key={v.flowId}
                          kind="video"
                          itemKey={v.flowId}
                          title={v.title}
                          summary={v.summary}
                          area={bundle?.area}
                          requiresPlan={v.requiresPlan ?? null}
                          onShown={onNudgeShown}
                          onUpgrade={goUpgrade}
                        />
                      ) : (
                        <VideoCard key={v.flowId} video={v} onPlay={(vid) => {
                          setPlaying(vid);
                          track('video_play', vid.flowId);
                        }} />
                      )))}
                    </div>
                  </section>
                )}

                {showDocs && filtered.docs.length > 0 && (
                  <section>
                    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Articles</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filtered.docs.map((d) => (d.locked ? (
                        <LockedCard
                          key={d.slug}
                          kind="doc"
                          itemKey={d.slug}
                          title={d.title}
                          summary={d.summary}
                          area={bundle?.area}
                          requiresPlan={d.requiresPlan ?? null}
                          onShown={onNudgeShown}
                          onUpgrade={goUpgrade}
                        />
                      ) : (
                        <DocCard key={d.slug} doc={d} onOpen={(doc) => { track('doc_view', doc.slug); setReading(doc); }} />
                      )))}
                    </div>
                  </section>
                )}

                {showTours && filtered.tours.length > 0 && (
                  <section>
                    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Guided tours</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {filtered.tours.map((t) => (t.locked ? (
                        <LockedCard
                          key={t.id}
                          kind="tour"
                          itemKey={t.id}
                          title={t.title}
                          summary={t.summary}
                          area={bundle?.area}
                          requiresPlan={t.requiresPlan ?? null}
                          onShown={onNudgeShown}
                          onUpgrade={goUpgrade}
                        />
                      ) : (
                        <TourCard key={t.id} tour={t} onStart={(tour) => track('tour_start', tour.id)} />
                      )))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {playing && <VideoModal video={playing} onClose={() => setPlaying(null)} />}
      {reading && <DocReader doc={reading} onClose={() => setReading(null)} />}
    </div>
  );
}

// ── Locked content card ────────────────────────────────────────────
// Renders a plan-gated doc/video/tour with a lock badge + an "Upgrade to <plan>" nudge
// instead of an open link. Fires `plan_nudge_shown` on display (deduped by the parent).
function LockedCard({
  kind, itemKey, title, summary, area, requiresPlan, onShown, onUpgrade,
}: {
  kind: 'doc' | 'video' | 'tour';
  itemKey: string;
  title: string;
  summary?: string | null;
  area?: string | null;
  requiresPlan: { id: string; name: string } | null;
  onShown: (key: string, planId: string | null, area?: string | null) => void;
  onUpgrade: (key: string, planId: string | null, area?: string | null) => void;
}) {
  useEffect(() => {
    onShown(itemKey, requiresPlan?.id ?? null, area);
  }, [itemKey, requiresPlan, area, onShown]);

  const kindLabel = kind === 'doc' ? 'Article' : kind === 'video' ? 'Video' : 'Guided tour';

  return (
    <div className="relative flex flex-col rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <span aria-hidden>🔒</span>
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{kindLabel}</span>
      </div>
      <h3 className="font-medium text-slate-900">{title}</h3>
      {summary && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{summary}</p>}
      <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
        {requiresPlan
          ? <>Upgrade to <span className="font-semibold">{requiresPlan.name}</span> to unlock.</>
          : <>Upgrade your plan to unlock this content.</>}
      </div>
      <button
        onClick={() => onUpgrade(itemKey, requiresPlan?.id ?? null, area)}
        className="mt-3 inline-flex items-center gap-1 self-start rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-brand-fg transition hover:opacity-90"
      >
        View plans →
      </button>
    </div>
  );
}

// ── Plans panel ────────────────────────────────────────────────────
function PlansPanel({ me, onEvent }: { me: Me; onEvent: (e: string, area?: string | null, meta?: unknown) => void }) {
  const [plans, setPlans] = useState<PlanContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!me.locationId) { setLoading(false); return; }
    setLoading(true);
    customerApi.planContext(me.locationId)
      .then((p) => { if (!cancelled) { setPlans(p); setLoading(false); } })
      .catch((e: Error) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [me.locationId]);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-44 animate-pulse rounded-xl bg-slate-200/70" />)}
      </div>
    );
  }

  const hasAny = plans && (plans.current || plans.upgrades.length > 0 || plans.downgrades.length > 0);
  if (error || !hasAny) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
        <div className="text-3xl">💳</div>
        <h3 className="mt-3 font-medium text-slate-900">No plan options to show</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
          {error
            ? 'We couldn’t load your plan right now. Please check back shortly.'
            : 'You’re all set — there are no plan changes available for your account at the moment.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Current plan */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Your plan</h2>
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-5">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-brand/10 text-xl">⭐</span>
          <div>
            <div className="text-lg font-semibold text-slate-900">{plans!.current?.name ?? 'No active plan'}</div>
            {plans!.group && <div className="text-xs text-slate-400">{plans!.group}</div>}
          </div>
          <span className="ml-auto rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">Current</span>
        </div>
      </section>

      {/* Upgrades */}
      {plans!.upgrades.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Upgrade</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans!.upgrades.map((p) => (
              <div key={p.id} className="flex flex-col rounded-xl border-2 border-brand/30 bg-white p-5 shadow-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-semibold text-slate-900">{p.name}</h3>
                  {p.price && <span className="text-sm font-semibold text-slate-700">{p.price}</span>}
                </div>
                {p.adds.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Adds</div>
                    <ul className="mt-1.5 space-y-1">
                      {p.adds.map((a) => (
                        <li key={a} className="flex items-center gap-2 text-sm text-slate-600">
                          <span className="text-emerald-500">✓</span>{prettySlug(a)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  onClick={() => onEvent('plan_upgrade_click', p.id, { plan: p.id, name: p.name })}
                  className="mt-4 w-full rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-brand-fg transition hover:opacity-90"
                >
                  Upgrade to {p.name}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Downgrades */}
      {plans!.downgrades.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Other plans</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans!.downgrades.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-4">
                <div>
                  <h3 className="font-medium text-slate-900">{p.name}</h3>
                  <span className="text-xs text-slate-400">Downgrade</span>
                </div>
                {p.price && <span className="text-sm font-medium text-slate-600">{p.price}</span>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function prettySlug(slug: string): string {
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Tickets panel ──────────────────────────────────────────────────
function TicketsPanel({ me, contextArea }: { me: Me; contextArea: string | null }) {
  const [meta, setMeta] = useState<TicketsMetaResponse | null>(null);
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<'list' | 'new'>('list');

  const loadTickets = useCallback(() => {
    customerApi.myTickets().then(setTickets).catch(() => setTickets([]));
  }, []);

  useEffect(() => {
    customerApi.ticketsMeta().then(setMeta).catch(() => setMeta({ statuses: [], priorities: [], categories: [] }));
    loadTickets();
  }, [loadTickets]);

  if (selectedId) {
    return <TicketDetailView id={selectedId} me={me} meta={meta} onBack={() => { setSelectedId(null); loadTickets(); }} />;
  }

  if (mode === 'new') {
    return (
      <TicketForm
        me={me}
        contextArea={contextArea}
        categories={meta?.categories ?? []}
        onCancel={() => setMode('list')}
        onCreated={() => { setMode('list'); loadTickets(); }}
      />
    );
  }

  const statusLabel = (id: string | null) => meta?.statuses.find((s) => s.id === id)?.label ?? 'Open';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Support tickets</h2>
          <p className="text-sm text-slate-500">Submit a request or track your open conversations.</p>
        </div>
        <button
          onClick={() => setMode('new')}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-brand-fg transition hover:opacity-90"
        >
          + New ticket
        </button>
      </div>

      {tickets === null && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-200/70" />)}
        </div>
      )}

      {tickets && tickets.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <div className="text-3xl">📨</div>
          <h3 className="mt-3 font-medium text-slate-900">No tickets yet</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Need a hand? Submit a ticket and our team will follow up by email.</p>
          <button onClick={() => setMode('new')} className="mt-4 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-brand-fg transition hover:opacity-90">
            Submit a ticket
          </button>
        </div>
      )}

      {tickets && tickets.length > 0 && (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {tickets.map((t) => (
            <li key={t.id}>
              <button onClick={() => setSelectedId(t.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-900">{t.subject}</div>
                  <div className="truncate text-xs text-slate-400">
                    {t.context_area ? `${t.context_area} · ` : ''}{fmtDate(t.created_at)}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{statusLabel(t.status_id)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TicketForm({
  me, contextArea, categories, onCancel, onCreated,
}: {
  me: Me;
  contextArea: string | null;
  categories: TicketsMetaResponse['categories'];
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      await customerApi.createTicket({
        subject: subject.trim(),
        body: body.trim(),
        category_id: categoryId || null,
        context_area: contextArea,
        context_route: typeof window !== 'undefined' ? window.location.pathname : null,
        requester_name: me.user.name,
        requester_email: me.user.email,
        requester_user_id: me.user.userId,
        location_id: me.locationId,
      });
      void customerApi.event({
        agencyId: me.agencyId, locationId: me.locationId, userId: me.user.userId,
        event: 'ticket_create', area: contextArea,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-xl space-y-4 rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Submit a ticket</h2>
        <button type="button" onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-800">← Back</button>
      </div>
      <p className="text-sm text-slate-500">We’ll follow up by email. Your current screen is attached so we have context.</p>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Subject</label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          placeholder="Briefly, what do you need help with?"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </div>

      {categories.length > 0 && (
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Category</label>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          >
            <option value="">General</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
      )}

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Details</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Describe what you need help with…"
          className="min-h-[120px] w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </div>

      {contextArea && (
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">📍 Attaching context: <span className="font-medium text-slate-700">{contextArea}</span></div>
      )}
      {err && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">Couldn’t submit: {err}</div>}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting || !subject.trim()}
          className="flex-1 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-brand-fg transition hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit ticket'}
        </button>
      </div>
    </form>
  );
}

function TicketDetailView({
  id, me, meta, onBack,
}: {
  id: string;
  me: Me;
  meta: TicketsMetaResponse | null;
  onBack: () => void;
}) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    customerApi.ticket(id)
      .then((d) => { setTicket(d.ticket); setComments(d.comments); setNotFound(!d.ticket); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const sendReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    // Optimistic append.
    const optimistic: TicketComment = {
      id: `tmp-${Date.now()}`,
      ticket_id: id,
      author_type: 'customer',
      author_name: me.user.name,
      body: reply.trim(),
      is_internal: false,
      created_at: new Date().toISOString(),
    };
    setComments((cs) => [...cs, optimistic]);
    const text = reply.trim();
    setReply('');
    try {
      await customerApi.addComment(id, {
        body: text,
        author_type: 'customer',
        author_name: me.user.name,
        is_internal: false,
      });
      load();
    } catch { /* keep optimistic bubble on failure */ }
    setSending(false);
  };

  const statusLabel = (sid: string | null) => meta?.statuses.find((s) => s.id === sid)?.label ?? 'Open';

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800">← All tickets</button>

      {loading && <div className="h-40 animate-pulse rounded-xl bg-slate-200/70" />}

      {!loading && notFound && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
          This ticket isn’t available.
        </div>
      )}

      {!loading && ticket && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-900">{ticket.subject}</h2>
              <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{statusLabel(ticket.status_id)}</span>
            </div>
            <div className="mt-1 text-xs text-slate-400">
              {ticket.context_area ? `${ticket.context_area} · ` : ''}Opened {fmtDate(ticket.created_at)}
            </div>
            {ticket.body && <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{ticket.body}</p>}
          </div>

          {/* Comment thread */}
          <div className="space-y-3">
            {comments.filter((c) => !c.is_internal).map((c) => (
              <div
                key={c.id}
                className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                  c.author_type === 'customer'
                    ? 'ml-auto bg-brand text-brand-fg'
                    : c.author_type === 'system'
                      ? 'mx-auto bg-slate-100 text-center text-xs text-slate-500'
                      : 'bg-white text-slate-700 ring-1 ring-slate-200'
                }`}
              >
                {c.author_type !== 'system' && c.author_name && (
                  <div className={`mb-0.5 text-[11px] font-semibold ${c.author_type === 'customer' ? 'text-white/80' : 'text-slate-400'}`}>{c.author_name}</div>
                )}
                <div className="whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
          </div>

          {/* Reply */}
          <form onSubmit={sendReply} className="rounded-xl border border-slate-200 bg-white p-3">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write a reply…"
              className="min-h-[70px] w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="submit"
                disabled={sending || !reply.trim()}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-brand-fg transition hover:opacity-90 disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

// See lib/time.ts — zone-less D1 timestamps must not go through `new Date(string)`.
const fmtDate = (iso: string) => formatApiDate(iso, '', { month: 'short', day: 'numeric', year: 'numeric' });
